import { useLicensingServiceV1 } from "@rw1/api-services";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { toHistoricalLicense } from "../../../../domain/mappers/licenseMappers";
import {
  type HistoricalLicense,
  isExternalLicense,
} from "../../../../domain/models/license";
import { GC_TIME_MS, STALE_TIME_MS } from "../../../../hooks/constants";
import { useCompaniesData } from "../../../../hooks/useCompaniesData";
import { useFetchAll } from "../../../../hooks/useFetchAll";
import { useLocalization } from "../../../../hooks/useLocalization";
import { formatDateYMD, hasDatePassed } from "../../../../shared/utils/date";

export const HISTORICAL_LICENSES_QUERY_KEY = "historicalLicenses";

interface UseHistoricalLicensesRawResult {
  licenses: HistoricalLicense[];
  isLoading: boolean;
  error: Error | null;
}

export const useHistoricalLicensesRaw = (
  enabled: boolean,
): UseHistoricalLicensesRawResult => {
  const licensingService = useLicensingServiceV1();
  const { companyId } = useParams();

  const {
    data: allLicenses = [],
    isLoading,
    error,
  } = useFetchAll<HistoricalLicense>(
    [HISTORICAL_LICENSES_QUERY_KEY, companyId],
    async (page, size) =>
      (
        await licensingService.getHistoricalLicensesByCompanyId(
          companyId!,
          page,
          size,
        )
      ).map(toHistoricalLicense),
    {
      enabled: enabled && !!companyId,
      staleTime: STALE_TIME_MS,
      gcTime: GC_TIME_MS,
    },
  );

  const licenses = useMemo(
    () =>
      companyId
        ? allLicenses.filter((license) => isExternalLicense(license, companyId))
        : [],
    [allLicenses, companyId],
  );

  return { licenses, isLoading, error };
};

interface UseHistoricalLicensesResult {
  licenses: HistoricalLicense[];
  licenseOptions: { value: string; label: string }[];
  isLoading: boolean;
  error: Error | null;
  licenseByIdMap: Record<string, HistoricalLicense>;
}

export const useHistoricalLicenses = (): UseHistoricalLicensesResult => {
  const { t } = useLocalization();
  const { companyNamesByIdMap, companiesLoading } = useCompaniesData();
  const {
    licenses,
    isLoading: isLicensesLoading,
    error,
  } = useHistoricalLicensesRaw(true);

  const licenseOptions = useMemo(
    () =>
      licenses
        .map((license) => {
          const companyName =
            companyNamesByIdMap[license.assignedOrganizationId] ?? "";

          if (
            !companyName &&
            !(license.assignedOrganizationId in companyNamesByIdMap)
          ) {
            return;
          }

          const isExpired = license.endDate
            ? hasDatePassed(license.endDate)
            : false;

          const dateRange = isExpired
            ? `${formatDateYMD(license.startDate)} (${t("pages.dataAnalytics.externalUsage.expired")})`
            : formatDateYMD(license.startDate);

          return {
            value: license.licenseId,
            label: `${companyName} - ${license.licenseName} - ${dateRange}`,
          };
        })
        .filter((e) => !!e)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [licenses, companyNamesByIdMap, t],
  );

  const licenseByIdMap = useMemo(() => {
    const map: Record<string, HistoricalLicense> = {};
    licenses.forEach((license) => {
      map[license.licenseId] = license;
    });

    return map;
  }, [licenses]);

  return {
    licenses,
    licenseOptions,
    isLoading: isLicensesLoading || companiesLoading,
    error,
    licenseByIdMap,
  };
};
